import { MercatorCoordinate, type Map as MapLibreMap } from 'maplibre-gl';
import { physicalCameraOptions, physicalCameraFollowZoom } from './physical-camera-optics';
import { CAMERA_TRACKS, evaluateAuthoredCameraTrack, cameraLensMillimeters, type CameraKeyframes, type CameraTrack } from '../domain/camera-keyframes';
import { cameraVerticalFieldOfView } from '../domain/camera-optics';
import type { CameraPose } from '../domain/camera-plan';
import type { CameraDistanceId } from '../domain/camera-distance';
import { readGimbalCamera } from './gimbal-camera-adapter';
import { solveNightHawkGeometry } from './night-hawk-camera-geometry';
import { terrainElevationAt } from './terrain-elevation';
import { recordCameraReadout } from './camera-readout';
import type { KeyframeCameraFollow } from '../domain/keyframe-camera-follow';
import { keyframeCameraAim } from './keyframe-camera-aim';
import { signedCameraOrientation } from './signed-camera-orientation';
import { applyMapFrameCoverage } from './map-frame-coverage';
import { platformRotation } from '../../../renderer/src/camera-heading';

const pitchLimits = new WeakMap<MapLibreMap, number>();

export type CameraAttributeValues = Record<CameraTrack, number>;
/** Deterministic automatic baseline for the first authored incoming transition. */
export function automaticCameraAttributes(pose: CameraPose, lens: number, height: number, width = height * 16 / 9): CameraAttributeValues {
  if (!pose.center) return { rotation: pose.bearing, tilt: pose.pitch, roll: pose.roll ?? 0, altitude: 1200, lens };
  const scale = MercatorCoordinate.fromLngLat([...pose.center], 0).meterInMercatorCoordinateUnits();
  const slant = height / (2 * Math.tan((pose.fieldOfView ?? cameraVerticalFieldOfView(lens, width, height)) * Math.PI / 360) * 512 * 2 ** pose.zoom);
  return { rotation: ((pose.bearing % 360) + 360) % 360, tilt: pose.pitch, roll: pose.roll ?? 0,
    altitude: Math.max(10, slant * Math.cos(pose.pitch * Math.PI / 180) / scale), lens };
}

/** Resolve sparse authored attributes after automatic motion and terrain gimbal have been applied. */
export async function applyAttributeCamera(map: MapLibreMap, tracks: CameraKeyframes, seconds: number,
  automaticAt: (seconds: number) => CameraAttributeValues, distance: CameraDistanceId,
  target: readonly [number, number], signal?: AbortSignal, follow?: KeyframeCameraFollow, headingWeight = 0) {
  const base = readGimbalCamera(map);
  const exaggeration = map.getTerrain()?.exaggeration ?? 1;
  const elevation = async (point: readonly [number, number], owner?: AbortSignal) => map.getTerrain() ? (await terrainElevationAt(point, owner)) * exaggeration : 0;
  const ground = await elevation(base.ground, signal);
  const values: CameraAttributeValues = { rotation: ((map.getBearing() % 360) + 360) % 360, tilt: map.getPitch(), roll: map.getRoll?.() ?? 0,
    altitude: Math.max(10, base.altitude - ground), lens: cameraLensMillimeters(distance) };
  const authored: Partial<CameraAttributeValues> = {};
  for (const axis of CAMERA_TRACKS) {
    const value = evaluateAuthoredCameraTrack(tracks, axis, seconds, time => automaticAt(time)[axis]);
    if (value !== undefined) authored[axis] = value;
  }
  const rotation = platformRotation(authored.rotation, automaticAt(seconds).rotation, headingWeight);
  if (rotation !== undefined) authored.rotation = rotation;
  if (!Object.keys(authored).length) { recordCameraReadout(map); return; }
  const resolved = { ...values, ...authored };
  const referenceZoom = physicalCameraFollowZoom(map, resolved.lens, resolved.altitude, resolved.tilt, target);
  const geometric = authored.rotation !== undefined || authored.tilt !== undefined || authored.altitude !== undefined;
  const aim = tracks.targetLock && geometric ? await keyframeCameraAim(follow, target, referenceZoom, elevation, signal)
    : { center: base.center, elevation: undefined };
  let physical = base;
  let renderedTilt = values.tilt;
  if (geometric) {
    const geometry = await solveNightHawkGeometry(aim.center, resolved, elevation, signal,
      aim.elevation);
    physical = { ground: [...geometry.ground], altitude: geometry.altitudeMsl, center: [...aim.center], aim: geometry.aim };
    renderedTilt = geometry.tilt;
  }
  signal?.throwIfAborted();
  if (!pitchLimits.has(map)) pitchLimits.set(map, map.getMaxPitch());
  map.setMaxPitch(90); map.setCenterClampedToGround(false);
  const options = physicalCameraOptions(map, physical, resolved.lens);
  map.jumpTo({ ...options, ...signedCameraOrientation(resolved.rotation, renderedTilt, resolved.roll), elevation: physical.aim });
  applyMapFrameCoverage(map);
  const data = map.getCanvas().dataset;
  data.cameraRotation = String(resolved.rotation); data.cameraTilt = String(renderedTilt); data.cameraRequestedTilt = String(resolved.tilt);
  data.cameraBank = String(resolved.roll);
  data.cameraAltitude = String(resolved.altitude); data.cameraLens = String(resolved.lens); data.cameraTargetLock = String(tracks.targetLock);
  data.cameraFramingLimited = 'false';
  const point = map.project([...target]); data.riderX = String(point.x); data.riderY = String(point.y);
  recordCameraReadout(map);
}


export async function recordCameraAttributes(map: MapLibreMap, distance: CameraDistanceId, signal?: AbortSignal) {
  const base = readGimbalCamera(map), data = map.getCanvas().dataset;
  const terrain = map.getTerrain() ? (await terrainElevationAt(base.ground, signal)) * (map.getTerrain()?.exaggeration ?? 1) : 0;
  signal?.throwIfAborted();
  data.cameraRotation = data.cameraRotation ?? String(((map.getBearing() % 360) + 360) % 360);
  data.cameraTilt = data.cameraTilt ?? String(map.getPitch());
  data.cameraBank = data.cameraBank ?? String(map.getRoll?.() ?? 0);
  data.cameraAltitude = String(base.altitude - terrain);
  data.cameraLens = data.cameraLens ?? String(cameraLensMillimeters(distance));
}
export function clearAttributeReadout(map: MapLibreMap) {
  const limit = pitchLimits.get(map); if (limit !== undefined) { map.setMaxPitch(limit); pitchLimits.delete(map); }
  const data = map.getCanvas().dataset;
  for (const key of ['cameraRotation','cameraTilt','cameraBank','cameraAltitude','cameraLens','cameraRequestedTilt','cameraTargetLock','cameraFramingLimited']) delete data[key];
}
